import React, { type ReactNode } from 'react'
import { cn, type UtilityClassNames } from '@s4l/utility-css'

import { Typography, Button, Tag } from '../../index'
import { IconCheck } from '@s4l/icons/IconCheck'
import { IconHiperlink } from '@s4l/icons/IconHiperlink'
import Feature from './Feature'

import styles from './index.module.css'

export interface OptionCardProps {
  className?: UtilityClassNames
  ctaLabel: string
  title: string
  priceText?: string
  children?: ReactNode
  selected: boolean
  tag?: string
  onSelect?: () => void
  onDetailsClick?: () => void
  detailsLabel?: string
}
function OptionCard({
  className,
  ctaLabel,
  title,
  priceText,
  children,
  selected = false,
  tag,
  onSelect,
  onDetailsClick,
  detailsLabel = 'All coverage details',
}: Readonly<OptionCardProps>) {
  if (!ctaLabel) {
    return null
  }

  const containerClasses = cn(
    styles.container,
    {
      [styles.selected]: selected,
    },
    className
  )

  return (
    <div className={styles.wrapper}>
      {tag && (
        <div className={styles.tagWrapper}>
          <Tag size="s">{tag}</Tag>
        </div>
      )}

      <div className={containerClasses} data-selected={selected}>
        <div className={styles.header}>
          {selected && (
            <div className={styles.checkIconWrapper}>
              <IconCheck color="var(--color-base-white)" size={12} />
            </div>
          )}

          <div className={styles.headerContent}>
            <Typography variant="body" size="l" bold clamp={2}>
              {title}
            </Typography>

            {priceText && (
              <div className={styles.pricing}>
                <Typography variant="body" size="l" bold>
                  + {priceText}
                </Typography>
              </div>
            )}
          </div>

          <Button
            variant="filled-rounded"
            customColor={selected ? 'var(--color-neutral-800)' : undefined}
            size="small"
            fullWidth
            onClick={onSelect}
          >
            {ctaLabel}
          </Button>
        </div>

        <div className={styles.body}>
          {children}

          {onDetailsClick && (
            <div className={styles.detailsButtonWrapper}>
              <Button
                variant="text-flat"
                size="extraSmall"
                onClick={onDetailsClick}
                trailingIcon={<IconHiperlink />}
              >
                {detailsLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

OptionCard.displayName = 'OptionCard'

OptionCard.Feature = Feature

export default OptionCard
